import { useEffect } from 'react';
import type { ReactElement } from 'react';

import InternalLinkingGc from '@skyscanner-internal/global-components/internal-links';
import { setPrerenderReady } from '@web-platform/shared-banana-utils-frontend/src/helpers/setPrerenderReady';
import { getInternalLinkingGcData } from '@web-platform/shared-banana-utils-frontend/src/singletons/seoComponents/internalLinkingGcData';
import { getInternationalSites } from '@web-platform/shared-banana-utils-frontend/src/singletons/seoComponents/internationalSites';
import { PRERENDER_DEPS } from '@web-platform/shared-universal-constants/src/homepage/prerenderDependencies';
import { SEO } from '@web-platform/shared-universal-constants/src/seo';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { translate } from '@web-platform/shared-universal-translations';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';

import { FAQs } from './FAQs/FAQs';
import InternationalSites from './InternationalSites';

import STYLES from './SeoComponents.module.scss';

const { SEO_CONTENT_ID } = SEO;

const SeoComponents = (): ReactElement | null => {
  useEffect(() => {
    setPrerenderReady(PRERENDER_DEPS.SEO);
  }, []);

  const internationalSites = getInternationalSites();
  const showInternationalSites =
    internationalSites.length !== 0 &&
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_INTERNATIONAL_SITES);
  const enableInternalLinking = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_INTERNAL_LINKING);
  const internalLinkingGcData = getInternalLinkingGcData();

  let internalLinksGC = null;

  if (enableInternalLinking && internalLinkingGcData?.internalLinks?.length) {
    const {
      a11yStrings,
      culture,
      internalLinks: gcInternalLinks,
      pageType,
    } = internalLinkingGcData;

    const internalLinkStrings = {
      indicatorLabel: '',
      prevNavLabel: a11yStrings?.prevNavLabel,
      nextNavLabel: a11yStrings?.nextNavLabel,
    };

    internalLinksGC = (
      <InternalLinkingGc
        title={translate('internal_linking_gc_subheading')}
        tabGroups={gcInternalLinks}
        a11yStrings={internalLinkStrings}
        messageProps={{ culture, pageType }}
      />
    );
  }

  if (!showInternationalSites && !enableInternalLinking) return null;

  return (
    <div data-testid={SEO_CONTENT_ID} className={STYLES.seoComponents}>
      <div>
        <FAQs />
        {showInternationalSites && <InternationalSites internationalSites={internationalSites} />}
      </div>
      {enableInternalLinking && internalLinksGC}
    </div>
  );
};

export default SeoComponents;
